<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch, getToken } from '../authService';
  import { t } from '../i18nStore';
  import { selectedCountry } from '../countryStore';
  import CountrySelector from '../components/CountrySelector.svelte';

  // Navigation tab state
  let activeTab = 'directory'; // 'directory' | 'csdi_matched' | 'csdi_unmatched' | 'track_records'

  let stores = [];
  let allCategories = [];
  let isLoading = true;
  let isSearching = false; // loading state for lookup
  let error = '';
  let successMessage = '';

  // Store form state
  let currentId = null;
  let currentName = '';
  let currentAliases = ''; // comma-separated list
  let currentCountry = $selectedCountry || 'HK';
  let currentCategories = [];
  let currentPlaces = [];
  let allPlaces = [];
  let currentLocations = []; 
  let currentActivationCode = '';
  let currentStoreProfileEnabled = false;
  let isEditing = false;

  function generateActivationCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    currentActivationCode = result;
  }

  // New location temp state
  let newLocAddress = '';
  let newLocLat = '';
  let newLocLng = '';

  // Track records & Filter state
  let filterText = '';
  let trackRecords = [];
  let trPage = 1;
  let trTotal = 0;
  let trLimit = 10;
  let isTrLoading = false;

  // React to country changes from navbar or page selector
  $: if ($selectedCountry) {
    if (!isEditing) {
      currentCountry = $selectedCountry;
    }
    if (typeof populateCountry !== 'undefined') {
      populateCountry = $selectedCountry;
    }
    if (typeof newStoreCountry !== 'undefined') {
      newStoreCountry = $selectedCountry;
    }
    fetchStores();
    fetchPlaces();
  }

  $: filteredStores = stores.filter(store => {
    const storeCountry = (store.countryCode || 'HK').toUpperCase();
    const targetCountry = ($selectedCountry || 'HK').toUpperCase();
    if (storeCountry !== targetCountry) return false;

    if (!filterText.trim()) return true;
    const term = filterText.toLowerCase();
    const nameMatch = store.name && store.name.toLowerCase().includes(term);
    const aliasMatch = store.aliases && store.aliases.some(alias => alias.toLowerCase().includes(term));
    return nameMatch || aliasMatch;
  });

  // Existing stores pagination
  let storePage = 1;
  const storePageSize = 50;
  $: if (filterText !== undefined || $selectedCountry !== undefined) {
    storePage = 1;
  }
  $: storeTotalPages = Math.ceil(filteredStores.length / storePageSize) || 1;
  $: paginatedStores = filteredStores.slice((storePage - 1) * storePageSize, storePage * storePageSize);

  // ==========================================
  // CSDI MATCHED STAGING QUEUE STATE
  // ==========================================
  let csdiFileInput;
  let isUploadingCSDI = false;
  let csdiUploadResult = null;
  let uploadPercent = 0;
  let uploadStatusText = '';
  let activeJob = null;
  let jobPollTimer = null;

  let stagedRecords = [];
  let stagedTotal = 0;
  let stagedPage = 1;
  let stagedLimit = 25;
  let stagedSummary = {
    pendingCount: 0,
    highConfidence: 0,
    needsReview: 0,
    appliedCount: 0,
    unmatchedCount: 0
  };
  let stagedStores = [];
  let stagedFilterStore = '';
  let stagedFilterStatus = 'pending';
  let stagedFilterConfidence = 'all';
  let stagedSearch = '';
  let isStagedLoading = false;
  let selectedStagedIds = new Set();
  let stagedOverrides = {}; // id -> { selectedPlace, selectedAction }

  // ==========================================
  // DISCOVERED SHOPPING MALLS STATE
  // ==========================================
  let discoveredPlaces = [];
  let isDiscoveringPlaces = false;
  let isAddingPlaces = false;
  let selectedDiscoveredPlaces = new Set();

  // ==========================================
  // UNMATCHED CSDI POI EXPLORER STATE
  // ==========================================
  let unmatchedRecords = [];
  let unmatchedTotal = 0;
  let unmatchedPage = 1;
  let unmatchedLimit = 25;
  let unmatchedSearch = '';
  let isUnmatchedLoading = false;

  // Create Store from Unmatched modal state
  let showCreateStoreModal = false;
  let newStoreRecord = null;
  let newStoreName = '';
  let newStoreAliases = '';
  let newStoreCountry = 'HK';
  let newStoreCategories = [];
  let newStorePlace = '';
  let newStoreAddress = '';
  let newStoreLat = 0;
  let newStoreLng = 0;
  let isCreatingStore = false;

  // ==========================================
  // COORDINATE AUTO-HEAL (BULKWRITE) STATE
  // ==========================================
  let corruptedStats = null;
  let isLoadingCorrupted = false;
  let isAutoHealing = false;
  let autoHealSuccessMsg = '';

  onMount(fetchInitialData);

  async function fetchInitialData() {
    isLoading = true;
    try {
      await Promise.all([
        fetchStores(),
        fetchCategories(),
        fetchPlaces(),
        fetchTrackRecords(1),
        fetchStagedRecords(1, false),
        checkRunningJob(),
        fetchDiscoveredPlaces(),
        fetchCorruptedStats()
      ]);
    } finally {
      isLoading = false;
    }
  }

  async function fetchCorruptedStats() {
    isLoadingCorrupted = true;
    try {
      const response = await authFetch(getApiUrl('/admin/stores/corrupted-coordinates'));
      if (response.ok) {
        corruptedStats = await response.json();
      }
    } catch (e) {
      console.error('Failed to fetch corrupted coordinates stats:', e);
    } finally {
      isLoadingCorrupted = false;
    }
  }

  async function triggerAutoHeal() {
    if (isAutoHealing) return;
    const confirmMsg = $t('admin.store.auto_heal_confirm') || 'Are you sure you want to execute bulk coordinate auto-heal on MongoDB using BulkWrite?';
    if (!window.confirm(confirmMsg)) return;

    isAutoHealing = true;
    error = '';
    autoHealSuccessMsg = '';
    try {
      const response = await authFetch(getApiUrl('/admin/stores/auto-heal-coordinates'), {
        method: 'POST'
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to auto-heal coordinates');
      }

      const s = data.stats || {};
      autoHealSuccessMsg = $t('admin.store.auto_heal_success', {
        total: s.totalHealed || 0,
        places: s.placesHealed || 0,
        csdi: s.csdiHealed || 0,
        stores: s.storesHealed || 0
      });

      await Promise.all([
        fetchCorruptedStats(),
        fetchStores(),
        fetchPlaces(),
        fetchStagedRecords(stagedPage || 1, false)
      ]);
    } catch (e) {
      error = e.message;
    } finally {
      isAutoHealing = false;
    }
  }

  async function fetchStores() {
    try {
      const code = $selectedCountry || 'HK';
      const response = await authFetch(getApiUrl(`/admin/stores?countryCode=${encodeURIComponent(code)}`));
      if (!response.ok) throw new Error('Could not load stores.');
      stores = await response.json();
    } catch (e) { error = e.message; }
  }

  async function fetchCategories() {
    try {
      const response = await fetch(getApiUrl('/categories'));
      if (response.ok) allCategories = await response.json();
    } catch (e) { console.error(e); }
  }

  async function fetchPlaces() {
    try {
      const code = $selectedCountry || 'HK';
      const response = await authFetch(getApiUrl(`/admin/places?countryCode=${encodeURIComponent(code)}`));
      if (response.ok) allPlaces = await response.json();
    } catch (e) { console.error(e); }
  }

  async function fetchTrackRecords(page = 1) {
    isTrLoading = true;
    trPage = page;
    try {
      const response = await authFetch(getApiUrl(`/admin/stores/track-records?page=${page}&limit=${trLimit}`));
      if (!response.ok) throw new Error('Could not load track records.');
      const data = await response.json();
      trackRecords = data.data || [];
      trTotal = data.total || 0;
    } catch (e) {
      console.error(e);
      error = e.message;
    } finally {
      isTrLoading = false;
    }
  }

  async function lookupAddress() {
    if (!newLocAddress.trim()) return;
    isSearching = true;
    error = '';
    
    try {
      const response = await authFetch(getApiUrl(`/admin/geocode?address=${encodeURIComponent(newLocAddress)}`));
      const data = await response.json();
      
      if (!response.ok) throw new Error(data.error || 'Lookup failed');
      
      newLocAddress = data.address;
      newLocLat = data.lat;
      newLocLng = data.lng;
    } catch (e) {
      error = e.message;
    } finally {
      isSearching = false;
    }
  }

  function addLocation() {
    if (!newLocAddress.trim() || !newLocLat || !newLocLng) return;
    
    currentLocations = [...currentLocations, {
      address: newLocAddress.trim(),
      coordinates: {
        type: 'Point',
        coordinates: [parseFloat(newLocLng), parseFloat(newLocLat)] 
      }
    }];

    newLocAddress = '';
    newLocLat = '';
    newLocLng = '';
  }

  function removeLocation(index) {
    currentLocations = currentLocations.filter((_, i) => i !== index);
  }

  function startEdit(store) {
    isEditing = true;
    currentId = store._id || store.id;
    currentName = store.name;
    currentAliases = store.aliases ? store.aliases.join(', ') : '';
    currentCountry = store.countryCode || 'HK';
    currentCategories = store.categories || [];
    currentPlaces = store.places || [];
    currentLocations = store.locations || [];
    currentActivationCode = store.activationCode || '';
    currentStoreProfileEnabled = store.storeProfileEnabled || false;
    successMessage = '';
    error = '';
    activeTab = 'directory';
  }

  function populateFromTrackRecord(record) {
    if (!record || !record.storeData) return;
    const s = record.storeData;
    isEditing = true;
    currentId = record.storeId || s._id || s.id || null;
    currentName = s.name || '';
    currentAliases = s.aliases ? s.aliases.join(', ') : '';
    currentCountry = s.countryCode || 'HK';
    currentCategories = s.categories || [];
    currentPlaces = s.places || [];
    currentLocations = s.locations || [];
    currentActivationCode = s.activationCode || '';
    currentStoreProfileEnabled = s.storeProfileEnabled || false;
    successMessage = `Populated form from track record for "${s.name}"`;
    error = '';
    activeTab = 'directory';
  }

  function cancelEdit() {
    isEditing = false;
    currentId = null;
    currentName = '';
    currentAliases = '';
    currentCountry = $selectedCountry || 'HK';
    currentCategories = [];
    currentPlaces = [];
    currentLocations = [];
    currentActivationCode = '';
    currentStoreProfileEnabled = false;
    newLocAddress = '';
    newLocLat = '';
    newLocLng = '';
  }

  async function handleSubmit() {
    if (!currentName.trim()) {
      error = 'Store name is required.';
      return;
    }
    
    const aliasesArray = currentAliases.split(',').map(s => s.trim()).filter(s => s !== '');
    
    const payload = { 
        name: currentName.trim(), 
        aliases: aliasesArray,
        countryCode: currentCountry.trim().toUpperCase(),
        categories: currentCategories,
        places: currentPlaces,
        locations: currentLocations,
        activationCode: currentActivationCode.trim(),
        storeProfileEnabled: currentStoreProfileEnabled
    };
    
    let url = getApiUrl('/admin/stores');
    let method = isEditing ? 'PUT' : 'POST';
    if (isEditing) url = getApiUrl(`/admin/stores/${currentId}`);

    try {
      const response = await authFetch(url, {
        method,
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
          const res = await response.json();
          throw new Error(res.error || 'Operation failed.');
      }
      
      successMessage = isEditing ? $t('admin.store.success_update') : $t('admin.store.success_add');
      cancelEdit();
      await Promise.all([fetchStores(), fetchTrackRecords(1)]);
    } catch (e) {
      error = e.message;
    }
  }

  async function handleDelete(id) {
    if (!confirm($t('admin.store.delete_confirm'))) return;
    try {
      const response = await authFetch(getApiUrl(`/admin/stores/${id}`), { method: 'DELETE' });
      if (response.ok) {
        successMessage = $t('admin.store.success_delete');
        await Promise.all([fetchStores(), fetchTrackRecords(1)]);
      }
    } catch (e) { error = e.message; }
  }

  // Populate from URLs/Content state
  let populateMode = 'url'; // 'url' | 'content'
  let englishUrl = '';
  let nonEnglishUrl = '';
  let englishContent = '';
  let nonEnglishContent = '';
  let predefinedAlias = '';
  let populateCategories = [];
  let populateCountry = 'HK';
  let isPopulating = false;

  async function handlePopulate() {
    if (populateMode === 'url' && !englishUrl.trim()) {
      error = 'English URL is required.';
      return;
    }
    if (populateMode === 'content' && !englishContent.trim()) {
      error = 'English webpage text or HTML source code is required.';
      return;
    }
    isPopulating = true;
    error = '';
    successMessage = '';
    try {
      const payload = {
        englishUrl: populateMode === 'url' ? englishUrl.trim() : '',
        nonEnglishUrl: populateMode === 'url' ? nonEnglishUrl.trim() : '',
        englishContent: populateMode === 'content' ? englishContent.trim() : '',
        nonEnglishContent: populateMode === 'content' ? nonEnglishContent.trim() : '',
        predefinedAlias: predefinedAlias.trim(),
        categories: populateCategories,
        countryCode: populateCountry.trim()
      };
      
      const response = await authFetch(getApiUrl('/admin/stores/populate-from-urls'), {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const res = await response.json();
      if (!response.ok) {
        throw new Error(res.error || 'Failed to populate stores.');
      }

      if (res.extracted === 0) {
        successMessage = res.message || 'No stores identified on the provided webpage(s).';
      } else {
        const template = $t('admin.store.success_populate') || 'Successfully populated stores! Extracted: {extracted}, Inserted: {inserted}, Skipped: {skipped}';
        successMessage = template
          .replace('{extracted}', res.extracted)
          .replace('{inserted}', res.inserted)
          .replace('{skipped}', res.skipped);
      }
      englishUrl = '';
      nonEnglishUrl = '';
      englishContent = '';
      nonEnglishContent = '';
      predefinedAlias = '';
      populateCategories = [];
      
      await Promise.all([fetchStores(), fetchTrackRecords(1)]);
    } catch (e) {
      error = e.message;
    } finally {
      isPopulating = false;
    }
  }

  // ==========================================
  // CSDI MATCHED QUEUE LOGIC
  // ==========================================

  async function checkRunningJob() {
    try {
      const res = await authFetch(getApiUrl('/admin/stores/csdi/job-status'));
      if (res.ok) {
        const job = await res.json();
        if (job && job.status === 'processing') {
          activeJob = job;
          isUploadingCSDI = true;
          pollJob(job.jobId);
        }
      }
    } catch {}
  }

  function pollJob(jobId) {
    if (jobPollTimer) clearInterval(jobPollTimer);
    jobPollTimer = setInterval(async () => {
      try {
        const statusRes = await authFetch(getApiUrl(`/admin/stores/csdi/job-status?jobId=${jobId}`));
        if (statusRes.ok) {
          const jobData = await statusRes.json();
          activeJob = jobData;

          if (jobData.status === 'completed') {
            clearInterval(jobPollTimer);
            jobPollTimer = null;
            isUploadingCSDI = false;
            successMessage = `🎉 ${jobData.stage || 'Import completed!'}`;
            if (csdiFileInput) csdiFileInput.value = '';
            await Promise.all([fetchStagedRecords(1), fetchStores(), fetchDiscoveredPlaces()]);
          } else if (jobData.status === 'failed') {
            clearInterval(jobPollTimer);
            jobPollTimer = null;
            isUploadingCSDI = false;
            error = jobData.errorMessage || 'Background processing failed';
          }
        }
      } catch (pollErr) {
        console.warn("CSDI job polling error:", pollErr);
      }
    }, 500);
  }

  async function handleCSDIUpload(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    isUploadingCSDI = true;
    error = '';
    successMessage = '';
    uploadPercent = 0;
    const mbSize = (file.size / (1024 * 1024)).toFixed(1);
    uploadStatusText = `Uploading ${mbSize} MB CSV file...`;
    activeJob = null;
    if (jobPollTimer) clearInterval(jobPollTimer);

    try {
      const token = await getToken();
      if (!token) throw new Error("Please log in to upload files.");

      const formData = new FormData();
      formData.append('file', file);

      // Upload with XHR to track network upload progress
      const uploadRes = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', getApiUrl('/admin/stores/csdi/upload'));
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const pct = Math.round((event.loaded / event.total) * 100);
            uploadPercent = pct;
            const loadedMB = (event.loaded / (1024 * 1024)).toFixed(1);
            uploadStatusText = `Uploading CSV: ${loadedMB} MB / ${mbSize} MB (${pct}%)...`;
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (err) {
              reject(new Error("Failed to parse server response"));
            }
          } else {
            try {
              const res = JSON.parse(xhr.responseText);
              reject(new Error(res.error || `Upload failed with status ${xhr.status}`));
            } catch {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          }
        };

        xhr.onerror = () => reject(new Error("Network connection error during file upload"));
        xhr.send(formData);
      });

      const jobId = uploadRes.jobId;
      uploadPercent = 100;
      uploadStatusText = "Upload complete! Initializing background processor...";

      activeJob = {
        jobId,
        fileName: file.name,
        status: 'processing',
        stage: 'Reading & matching records in memory...',
        percent: 10,
        totalRows: 0,
        processedRows: 0,
        matchedRows: 0,
        unmatchedRows: 0,
        highConfidence: 0,
      };

      pollJob(jobId);
    } catch (err) {
      error = err.message;
      isUploadingCSDI = false;
      uploadStatusText = '';
      if (jobPollTimer) clearInterval(jobPollTimer);
    }
  }

  async function fetchStagedRecords(page = 1, showSpinner = true) {
    if (showSpinner) isStagedLoading = true;
    stagedPage = page;
    selectedStagedIds = new Set();

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: stagedLimit.toString(),
        status: stagedFilterStatus,
      });
      if (stagedFilterConfidence !== 'all') params.append('confidence', stagedFilterConfidence);
      if (stagedFilterStore) params.append('storeName', stagedFilterStore);
      if (stagedSearch.trim()) params.append('search', stagedSearch.trim());

      const res = await authFetch(getApiUrl(`/admin/stores/csdi/staged?${params.toString()}`));
      if (!res.ok) throw new Error('Failed to load staged records');

      const data = await res.json();
      stagedRecords = data.data || [];
      stagedTotal = data.total || 0;
      stagedSummary = data.summary || stagedSummary;
      stagedStores = data.stores || [];

      // Prepopulate default overrides
      stagedOverrides = {};
      stagedRecords.forEach(r => {
        stagedOverrides[r._id] = {
          selectedPlace: r.selectedPlace || r.proposedPlaceName || '',
          selectedAction: r.selectedAction || r.suggestedAction || 'STREET',
        };
      });
    } catch (err) {
      console.error(err);
      error = err.message;
    } finally {
      isStagedLoading = false;
    }
  }

  function toggleSelectAllStaged() {
    if (selectedStagedIds.size === stagedRecords.length) {
      selectedStagedIds = new Set();
    } else {
      selectedStagedIds = new Set(stagedRecords.map(r => r._id));
    }
  }

  function toggleSelectStaged(id) {
    const next = new Set(selectedStagedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selectedStagedIds = next;
  }

  function handleMallOverride(recId, placeName) {
    if (!stagedOverrides[recId]) stagedOverrides[recId] = {};
    stagedOverrides[recId].selectedPlace = placeName;
    stagedOverrides[recId].selectedAction = placeName ? 'MALL' : 'STREET';
    stagedOverrides = { ...stagedOverrides };
  }

  async function quickApplyHighConfidence() {
    if (!confirm(`Apply all ${stagedSummary.highConfidence} High-Confidence locations immediately?`)) return;

    isStagedLoading = true;
    error = '';
    successMessage = '';

    try {
      const payload = {};
      if (stagedFilterStore) payload.storeName = stagedFilterStore;

      const res = await authFetch(getApiUrl('/admin/stores/csdi/quick-apply'), {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to apply high-confidence locations');

      successMessage = `Successfully applied ${data.appliedCount} high-confidence locations!`;
      await Promise.all([fetchStagedRecords(1), fetchStores(), fetchTrackRecords(1)]);
    } catch (err) {
      error = err.message;
    } finally {
      isStagedLoading = false;
    }
  }

  async function applySelectedStaged() {
    if (selectedStagedIds.size === 0) return;

    isStagedLoading = true;
    error = '';
    successMessage = '';

    try {
      const recordIds = Array.from(selectedStagedIds);
      const overrides = {};
      recordIds.forEach(id => {
        if (stagedOverrides[id]) overrides[id] = stagedOverrides[id];
      });

      const res = await authFetch(getApiUrl('/admin/stores/csdi/apply-selected'), {
        method: 'POST',
        body: JSON.stringify({ recordIds, overrides }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to apply selected locations');

      successMessage = `Applied ${data.appliedCount} locations successfully!`;
      await Promise.all([fetchStagedRecords(stagedPage), fetchStores(), fetchTrackRecords(1)]);
    } catch (err) {
      error = err.message;
    } finally {
      isStagedLoading = false;
    }
  }

  async function rejectSelectedStaged() {
    if (selectedStagedIds.size === 0) return;
    if (!confirm(`Reject and dismiss ${selectedStagedIds.size} selected record(s)?`)) return;

    isStagedLoading = true;
    try {
      const recordIds = Array.from(selectedStagedIds);
      const res = await authFetch(getApiUrl('/admin/stores/csdi/reject'), {
        method: 'POST',
        body: JSON.stringify({ recordIds }),
      });
      if (!res.ok) throw new Error('Failed to reject records');

      successMessage = `Rejected ${recordIds.length} records.`;
      await fetchStagedRecords(stagedPage);
    } catch (err) {
      error = err.message;
    } finally {
      isStagedLoading = false;
    }
  }

  async function applySingleStaged(record) {
    isStagedLoading = true;
    try {
      const overrides = {};
      if (stagedOverrides[record._id]) overrides[record._id] = stagedOverrides[record._id];

      const res = await authFetch(getApiUrl('/admin/stores/csdi/apply-selected'), {
        method: 'POST',
        body: JSON.stringify({ recordIds: [record._id], overrides }),
      });
      if (!res.ok) throw new Error('Failed to apply record');

      successMessage = `Applied location for "${record.storeName}".`;
      await Promise.all([fetchStagedRecords(stagedPage), fetchStores(), fetchTrackRecords(1)]);
    } catch (err) {
      error = err.message;
    } finally {
      isStagedLoading = false;
    }
  }

  async function rejectSingleStaged(record) {
    try {
      const res = await authFetch(getApiUrl('/admin/stores/csdi/reject'), {
        method: 'POST',
        body: JSON.stringify({ recordIds: [record._id] }),
      });
      if (!res.ok) throw new Error('Failed to reject record');
      await fetchStagedRecords(stagedPage);
    } catch (err) {
      error = err.message;
    }
  }

  // ==========================================
  // DISCOVERED SHOPPING MALLS LOGIC
  // ==========================================

  async function fetchDiscoveredPlaces() {
    isDiscoveringPlaces = true;
    try {
      const params = new URLSearchParams();
      const batchId = activeJob?.batchId || (stagedRecords[0]?.batchId) || '';
      if (batchId) params.append('batchId', batchId);

      const res = await authFetch(getApiUrl(`/admin/stores/csdi/discovered-places?${params.toString()}`));
      if (res.ok) {
        const data = await res.json();
        discoveredPlaces = data.places || [];
        selectedDiscoveredPlaces = new Set(discoveredPlaces.map((_, i) => i));
      }
    } catch (err) {
      console.warn("Failed to fetch discovered places:", err);
    } finally {
      isDiscoveringPlaces = false;
    }
  }

  function toggleSelectAllDiscovered() {
    if (selectedDiscoveredPlaces.size === discoveredPlaces.length) {
      selectedDiscoveredPlaces = new Set();
    } else {
      selectedDiscoveredPlaces = new Set(discoveredPlaces.map((_, i) => i));
    }
    selectedDiscoveredPlaces = selectedDiscoveredPlaces;
  }

  function toggleSelectDiscovered(index) {
    const next = new Set(selectedDiscoveredPlaces);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    selectedDiscoveredPlaces = next;
  }

  async function addSelectedDiscoveredPlaces() {
    if (selectedDiscoveredPlaces.size === 0) return;
    isAddingPlaces = true;
    error = '';
    successMessage = '';

    try {
      const selectedPlacesList = Array.from(selectedDiscoveredPlaces).map(idx => discoveredPlaces[idx]);
      const batchId = activeJob?.batchId || (stagedRecords[0]?.batchId) || '';

      const res = await authFetch(getApiUrl('/admin/stores/csdi/add-discovered-places'), {
        method: 'POST',
        body: JSON.stringify({
          batchId,
          places: selectedPlacesList,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add discovered places');

      successMessage = `🎉 ${data.message || `Created ${selectedPlacesList.length} shopping malls and re-matched store locations!`}`;

      await Promise.all([
        fetchPlaces(),
        fetchStagedRecords(1),
        fetchDiscoveredPlaces(),
      ]);
    } catch (err) {
      error = err.message;
    } finally {
      isAddingPlaces = false;
    }
  }

  async function addSingleDiscoveredPlace(mall) {
    isAddingPlaces = true;
    error = '';
    successMessage = '';

    try {
      const batchId = activeJob?.batchId || (stagedRecords[0]?.batchId) || '';
      const res = await authFetch(getApiUrl('/admin/stores/csdi/add-discovered-places'), {
        method: 'POST',
        body: JSON.stringify({
          batchId,
          places: [mall],
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add place');

      successMessage = `Created "${mall.name}" and re-matched ${data.reMatchedCount || 0} store locations!`;
      await Promise.all([
        fetchPlaces(),
        fetchStagedRecords(stagedPage),
        fetchDiscoveredPlaces(),
      ]);
    } catch (err) {
      error = err.message;
    } finally {
      isAddingPlaces = false;
    }
  }

  // ==========================================
  // UNMATCHED CSDI LOGIC ("CREATE NEW STORE")
  // ==========================================

  async function fetchUnmatchedRecords(page = 1) {
    isUnmatchedLoading = true;
    unmatchedPage = page;

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: unmatchedLimit.toString(),
        status: 'unmatched',
      });
      if (unmatchedSearch.trim()) params.append('search', unmatchedSearch.trim());

      const res = await authFetch(getApiUrl(`/admin/stores/csdi/staged?${params.toString()}`));
      if (!res.ok) throw new Error('Failed to load unmatched records');

      const data = await res.json();
      unmatchedRecords = data.data || [];
      unmatchedTotal = data.total || 0;
      stagedSummary = data.summary || stagedSummary;
    } catch (err) {
      console.error(err);
      error = err.message;
    } finally {
      isUnmatchedLoading = false;
    }
  }

  function openCreateStoreModal(rec) {
    newStoreRecord = rec;
    newStoreName = rec.csdiNameEn || rec.csdiNameZh || '';
    newStoreAliases = rec.csdiNameZh && rec.csdiNameZh !== newStoreName ? rec.csdiNameZh : '';
    newStoreCountry = 'HK';
    newStoreCategories = [];
    newStorePlace = rec.proposedPlaceName || '';
    newStoreAddress = rec.addressEn || rec.addressZh || '';
    newStoreLat = (rec.coordinates && rec.coordinates.coordinates) ? rec.coordinates.coordinates[1] : 0;
    newStoreLng = (rec.coordinates && rec.coordinates.coordinates) ? rec.coordinates.coordinates[0] : 0;
    showCreateStoreModal = true;
    error = '';
  }

  function closeCreateStoreModal() {
    showCreateStoreModal = false;
    newStoreRecord = null;
  }

  async function submitCreateStoreFromUnmatched() {
    if (!newStoreName.trim()) {
      error = 'Store name is required';
      return;
    }

    isCreatingStore = true;
    error = '';

    try {
      const aliasesArray = newStoreAliases.split(',').map(s => s.trim()).filter(Boolean);
      const payload = {
        recordId: newStoreRecord._id,
        name: newStoreName.trim(),
        aliases: aliasesArray,
        countryCode: newStoreCountry.trim().toUpperCase(),
        categories: newStoreCategories,
        place: newStorePlace.trim(),
        address: newStoreAddress.trim(),
        lat: parseFloat(newStoreLat) || 0,
        lng: parseFloat(newStoreLng) || 0,
      };

      const res = await authFetch(getApiUrl('/admin/stores/csdi/create-store'), {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create store from CSDI record');

      successMessage = `Successfully created store "${data.store.name}" with its initial location!`;
      closeCreateStoreModal();
      await Promise.all([
        fetchStores(),
        fetchUnmatchedRecords(unmatchedPage),
        fetchStagedRecords(1, false),
        fetchTrackRecords(1)
      ]);
    } catch (err) {
      error = err.message;
    } finally {
      isCreatingStore = false;
    }
  }
</script>

<div class="maintenance-container">
  <h2>{$t('admin.store.title')}</h2>

  <!-- Top Navigation Tabs -->
  <div class="tab-bar">
    <button 
      class="tab-btn {activeTab === 'directory' ? 'active' : ''}" 
      on:click={() => activeTab = 'directory'}>
      🏪 {$t('admin.store.tab_directory')}
    </button>
    
    <button 
      class="tab-btn {activeTab === 'csdi_matched' ? 'active' : ''}" 
      on:click={() => { activeTab = 'csdi_matched'; fetchStagedRecords(1); fetchDiscoveredPlaces(); }}>
      📍 {$t('admin.store.tab_csdi_matched')}
      {#if stagedSummary.pendingCount > 0}
        <span class="tab-badge">{stagedSummary.pendingCount}</span>
      {/if}
      {#if discoveredPlaces.length > 0}
        <span class="tab-badge purple" title="{discoveredPlaces.length} discovered missing malls">🏬 {discoveredPlaces.length}</span>
      {/if}
    </button>

    <button 
      class="tab-btn {activeTab === 'csdi_unmatched' ? 'active' : ''}" 
      on:click={() => { activeTab = 'csdi_unmatched'; fetchUnmatchedRecords(1); }}>
      🔍 {$t('admin.store.tab_csdi_unmatched')}
      {#if stagedSummary.unmatchedCount > 0}
        <span class="tab-badge gray">{stagedSummary.unmatchedCount}</span>
      {/if}
    </button>

    <button 
      class="tab-btn {activeTab === 'track_records' ? 'active' : ''}" 
      on:click={() => activeTab = 'track_records'}>
      📋 {$t('admin.store.tab_track_records')} ({trTotal})
    </button>
  </div>

  {#if successMessage}<p class="success alert-msg">{successMessage}</p>{/if}
  {#if error}<p class="error alert-msg">{error}</p>{/if}

  <!-- Coordinate Auto-Heal Banner -->
  {#if corruptedStats && corruptedStats.totalCount > 0}
    <div class="card auto-heal-banner warning">
      <div class="auto-heal-content">
        <div class="auto-heal-info">
          <span class="auto-heal-icon">⚠️</span>
          <div>
            <h4>{$t('admin.store.auto_heal_title')}</h4>
            <p class="auto-heal-desc">
              {$t('admin.store.auto_heal_corrupted_found', {
                total: corruptedStats.totalCount,
                places: corruptedStats.placesCount,
                csdi: corruptedStats.csdiCount,
                stores: corruptedStats.storesCount
              })}
            </p>
          </div>
        </div>
        <div class="auto-heal-actions">
          <button 
            class="auto-heal-btn btn-warning" 
            on:click={triggerAutoHeal} 
            disabled={isAutoHealing}>
            {#if isAutoHealing}
              <span class="spinner-small"></span> {$t('admin.store.auto_heal_running')}
            {:else}
              ⚡ {$t('admin.store.auto_heal_btn')}
            {/if}
          </button>
        </div>
      </div>
    </div>
  {/if}

  {#if autoHealSuccessMsg}
    <div class="card auto-heal-banner success">
      <div class="auto-heal-content">
        <div class="auto-heal-info">
          <span class="auto-heal-icon">✅</span>
          <div>
            <h4>{$t('admin.store.auto_heal_title')}</h4>
            <p class="auto-heal-desc">{autoHealSuccessMsg}</p>
          </div>
        </div>
        <button class="small-btn" on:click={() => autoHealSuccessMsg = ''}>✕</button>
      </div>
    </div>
  {/if}

  <!-- ============================================================== -->
  <!-- TAB 1: STORE DIRECTORY & EDITOR                                -->
  <!-- ============================================================== -->
  {#if activeTab === 'directory'}
    <div class="grid-layout">
      <div class="action-column">
          <div class="card form-section">
              <h3>{isEditing ? $t('admin.store.edit') : $t('admin.store.add')}</h3>
              <form on:submit|preventDefault={handleSubmit}>
                  <div class="form-group">
                      <label for="store-name">{$t('admin.store.name')}</label>
                      <input type="text" id="store-name" bind:value={currentName} required />
                  </div>
                  <div class="form-group">
                      <label for="store-aliases">{$t('admin.store.aliases')}</label>
                      <input type="text" id="store-aliases" bind:value={currentAliases} placeholder={$t('admin.store.aliases_placeholder')} />
                  </div>
                  <div class="form-group">
                      <label for="country-code">{$t('admin.store.country')}</label>
                      <input type="text" id="country-code" bind:value={currentCountry} required />
                  </div>
                  <div class="form-group">
                      <label for="activation-code">{$t('admin.store.activation_code')}</label>
                      <div style="display: flex; gap: 0.5rem; align-items: center;">
                          <input type="text" id="activation-code" bind:value={currentActivationCode} maxlength="6" style="flex: 1;" placeholder="e.g. A1B2C3" />
                          <button type="button" class="secondary" on:click={generateActivationCode} style="padding: 0.5rem 1rem; border-radius: 4px; border: 1px solid #ccc; cursor: pointer; background: #f0f0f0;">{$t('admin.store.generate_code')}</button>
                      </div>
                  </div>
                  <div class="form-group">
                      <label class="checkbox-label" style="display: flex; align-items: center; gap: 0.5rem;">
                          <input type="checkbox" bind:checked={currentStoreProfileEnabled} />
                          <span>{$t('admin.store.enable_profile')}</span>
                      </label>
                  </div>
                  
                  <div class="form-group">
                      <label>{$t('admin.categories')}</label>
                      <div class="category-scroll">
                          {#each allCategories as cat}
                              <label class="checkbox-label">
                                  <input type="checkbox" value={cat.name} bind:group={currentCategories} />
                                  <span>{cat.name}</span>
                              </label>
                          {/each}
                      </div>
                  </div>

                  <div class="form-group">
                      <label>{$t('admin.store.places')}</label>
                      <div class="category-scroll">
                          {#if allPlaces.length === 0}
                              <span class="empty-hint" style="color: #888; font-size: 0.85rem; font-style: italic;">{$t('admin.store.no_places')}</span>
                          {/if}
                          {#each allPlaces as pl}
                              <label class="checkbox-label">
                                  <input type="checkbox" value={pl.name} bind:group={currentPlaces} />
                                  <span>{pl.nameEn || pl.name}{pl.nameZh && pl.nameZh !== (pl.nameEn || pl.name) ? ` (${pl.nameZh})` : ''} ({pl.countryCode})</span>
                              </label>
                          {/each}
                      </div>
                  </div>

                  <div class="locations-section">
                      <label>{$t('admin.store.locations')}</label>
                      
                      <div class="location-list">
                          {#each currentLocations as loc, i}
                              <div class="location-pill">
                                  <span class="loc-addr">{loc.address}{loc.place ? ` [${loc.place}]` : ''}</span>
                                  <button type="button" class="remove-loc" on:click={() => removeLocation(i)}>&times;</button>
                              </div>
                          {/each}
                      </div>

                      <div class="location-add-form">
                          <div class="address-lookup-row">
                              <input type="text" bind:value={newLocAddress} placeholder={$t('admin.store.address')} />
                              <button type="button" class="lookup-btn" on:click={lookupAddress} disabled={isSearching || !newLocAddress}>
                                  {isSearching ? '...' : '🔍'}
                              </button>
                          </div>
                          <div class="coord-row">
                              <input type="number" step="any" bind:value={newLocLat} placeholder={$t('admin.store.lat')} />
                              <input type="number" step="any" bind:value={newLocLng} placeholder={$t('admin.store.lng')} />
                              <button type="button" class="add-loc-btn" on:click={addLocation}>+</button>
                          </div>
                      </div>
                  </div>

                  <div class="form-actions">
                      {#if isEditing}
                          <button type="button" class="secondary" on:click={cancelEdit}>{$t('common.cancel')}</button>
                      {/if}
                      <button type="submit" class="primary">{isEditing ? $t('admin.store.save') : $t('admin.store.add_btn')}</button>
                  </div>
              </form>
          </div>

          <div class="card populate-section" style="margin-top: 1.5rem;">
              <h3>{$t('admin.store.populate_title')}</h3>

              <!-- Input mode toggle -->
              <div class="populate-mode-toggle" style="display: flex; gap: 0.5rem; margin-bottom: 1.25rem;">
                  <button type="button" class="mode-tab-btn {populateMode === 'url' ? 'active' : ''}" on:click={() => populateMode = 'url'}>
                      🌐 {$t('admin.store.input_mode_url')}
                  </button>
                  <button type="button" class="mode-tab-btn {populateMode === 'content' ? 'active' : ''}" on:click={() => populateMode = 'content'}>
                      📋 {$t('admin.store.input_mode_content')}
                  </button>
              </div>

              <form on:submit|preventDefault={handlePopulate}>
                  {#if populateMode === 'url'}
                      <div class="form-group">
                          <label for="english-url">{$t('admin.store.english_url')}</label>
                          <input type="text" id="english-url" bind:value={englishUrl} placeholder="https://www.example.com/en/brands" required />
                      </div>
                      <div class="form-group">
                          <label for="non-english-url">{$t('admin.store.non_english_url')}</label>
                          <input type="text" id="non-english-url" bind:value={nonEnglishUrl} placeholder="https://www.example.com/zh/brands" />
                      </div>
                  {:else}
                      <div class="info-callout" style="background: #eef7ff; border: 1px solid #c9e2ff; border-radius: 6px; padding: 0.75rem 0.9rem; margin-bottom: 1.2rem; font-size: 0.85rem; color: #0056b3; line-height: 1.4;">
                          ℹ️ {$t('admin.store.content_hint')}
                      </div>
                      <div class="form-group">
                          <label for="english-content">{$t('admin.store.english_content')}</label>
                          <textarea id="english-content" bind:value={englishContent} rows="6" placeholder="Paste rendered webpage text or HTML source code here..." required style="width: 100%; box-sizing: border-box; font-family: monospace; font-size: 0.85rem; padding: 0.5rem; border: 1px solid #ccc; border-radius: 4px;"></textarea>
                      </div>
                      <div class="form-group">
                          <label for="non-english-content">{$t('admin.store.non_english_content')}</label>
                          <textarea id="non-english-content" bind:value={nonEnglishContent} rows="6" placeholder="Paste Chinese / Non-English webpage text or HTML source code here (optional)..." style="width: 100%; box-sizing: border-box; font-family: monospace; font-size: 0.85rem; padding: 0.5rem; border: 1px solid #ccc; border-radius: 4px;"></textarea>
                      </div>
                  {/if}

                  <div class="form-group">
                      <label for="populate-predefined-alias">{$t('admin.store.predefined_alias')}</label>
                      <input type="text" id="populate-predefined-alias" bind:value={predefinedAlias} placeholder="e.g. Cafe Deco Group" />
                  </div>
                  <div class="form-group">
                      <label for="populate-country-code">{$t('admin.store.country')}</label>
                      <input type="text" id="populate-country-code" bind:value={populateCountry} required />
                  </div>
                  <div class="form-group">
                      <label>{$t('admin.store.predefined_categories')}</label>
                      <div class="category-scroll">
                          {#each allCategories as cat}
                              <label class="checkbox-label">
                                  <input type="checkbox" value={cat.name} bind:group={populateCategories} />
                                  <span>{cat.name}</span>
                              </label>
                          {/each}
                      </div>
                  </div>
                  <div class="form-actions">
                      <button type="submit" class="primary" disabled={isPopulating}>
                          {isPopulating ? $t('admin.store.extracting') : $t('admin.store.extract_save')}
                      </button>
                  </div>
              </form>
          </div>
      </div>

      <div class="list-column">
        <div class="card list-section">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; flex-wrap: wrap; gap: 0.5rem;">
            <h3 style="margin: 0;">{$t('admin.store.existing')} ({filteredStores.length})</h3>
            <div style="display: flex; align-items: center; gap: 0.5rem;">
              <CountrySelector />
            </div>
          </div>
          <div class="search-filter-box" style="margin-bottom: 1.5rem;">
            <input type="text" placeholder={$t('admin.store.search_placeholder')} bind:value={filterText} style="width: 100%; padding: 0.6rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box;" />
          </div>
          {#if isLoading}
            <p>{$t('common.loading')}</p>
          {:else if filteredStores.length === 0}
            <p>{$t('admin.store.none')}</p>
          {:else}
            <div class="list-wrapper">
              <ul>
                {#each paginatedStores as store (store._id || store.id)}
                  <li>
                    <div class="info">
                      <div>
                          <span class="store-name">{store.name}</span>
                          <span class="country-badge">{store.countryCode}</span>
                          {#if store.aliases && store.aliases.length > 0}
                              <span class="aliases-badge">{store.aliases.join(', ')}</span>
                          {/if}
                      </div>
                      <span class="loc-count">
                          {store.places ? store.places.length : 0} {$t('admin.store.places_label')} &bull; 
                          {store.locations ? store.locations.length : 0} {$t('admin.store.locations_label')}
                      </span>
                    </div>
                    <div class="item-actions">
                      <button class="edit-btn" on:click={() => startEdit(store)}>{$t('common.edit')}</button>
                      <button class="delete-btn" on:click={() => handleDelete(store._id || store.id)}>{$t('common.delete')}</button>
                    </div>
                  </li>
                {/each}
              </ul>
            </div>

            {#if storeTotalPages > 1}
              <div class="pagination-row" style="margin-top: 1rem; display: flex; justify-content: space-between; align-items: center;">
                <button class="secondary" disabled={storePage === 1} on:click={() => storePage--} style="padding: 4px 10px; font-size: 0.85rem;">
                  &larr; {$t('common.prev')}
                </button>
                <span style="font-size: 0.85rem; color: #555;">
                  {$t('admin.store.showing_range', {
                    start: (storePage - 1) * storePageSize + 1,
                    end: Math.min(storePage * storePageSize, filteredStores.length),
                    total: filteredStores.length
                  })} &bull; {$t('common.page_of', { page: storePage, total: storeTotalPages })}
                </span>
                <button class="secondary" disabled={storePage >= storeTotalPages} on:click={() => storePage++} style="padding: 4px 10px; font-size: 0.85rem;">
                  {$t('common.next')} &rarr;
                </button>
              </div>
            {/if}
          {/if}
        </div>
      </div>
    </div>
  {/if}

  <!-- ============================================================== -->
  <!-- TAB 2: CSDI LOCATION IMPORTER (MATCHED STAGING QUEUE)          -->
  <!-- ============================================================== -->
  {#if activeTab === 'csdi_matched'}
    <div class="csdi-container">
      <!-- Upload Card -->
      <div class="card upload-box">
        <div class="upload-header">
          <div>
            <h3>📁 {$t('admin.csdi.upload_title')}</h3>
            <p class="hint">{$t('admin.csdi.upload_hint')}</p>
          </div>
          <div class="upload-controls">
            <input 
              type="file" 
              accept=".csv" 
              bind:this={csdiFileInput} 
              on:change={handleCSDIUpload} 
              disabled={isUploadingCSDI}
              id="csdi-file-input"
              style="display: none;" 
            />
            <label for="csdi-file-input" class="upload-btn {isUploadingCSDI ? 'disabled' : ''}">
              {isUploadingCSDI ? $t('admin.csdi.processing_bg') : $t('admin.csdi.choose_csv')}
            </label>
          </div>
        </div>
      </div>

      <!-- MongoDB Coordinate Health & BulkWrite Auto-Heal Card -->
      <div class="card auto-heal-card">
        <div class="auto-heal-card-header">
          <div>
            <h4>🛠️ {$t('admin.store.auto_heal_title')}</h4>
            <p class="hint">{$t('admin.store.auto_heal_desc')}</p>
          </div>
          <div class="auto-heal-card-actions">
            <button class="secondary" on:click={fetchCorruptedStats} disabled={isLoadingCorrupted}>
              🔄 {$t('admin.store.auto_heal_refresh')}
            </button>
            <button 
              class="primary {corruptedStats && corruptedStats.totalCount > 0 ? 'pulse-btn' : ''}" 
              on:click={triggerAutoHeal} 
              disabled={isAutoHealing}>
              {#if isAutoHealing}
                <span class="spinner-small"></span> {$t('admin.store.auto_heal_running')}
              {:else}
                ⚡ {$t('admin.store.auto_heal_btn')}
              {/if}
            </button>
          </div>
        </div>
        {#if corruptedStats}
          <div class="health-stats-row">
            <span class="stat-pill {corruptedStats.placesCount > 0 ? 'pill-warning' : 'pill-ok'}">
              🏬 Places: {corruptedStats.placesCount > 0 ? `${corruptedStats.placesCount} corrupted` : '0 (OK)'}
            </span>
            <span class="stat-pill {corruptedStats.csdiCount > 0 ? 'pill-warning' : 'pill-ok'}">
              📍 CSDI Staged: {corruptedStats.csdiCount > 0 ? `${corruptedStats.csdiCount} corrupted` : '0 (OK)'}
            </span>
            <span class="stat-pill {corruptedStats.storesCount > 0 ? 'pill-warning' : 'pill-ok'}">
              🏪 Stores: {corruptedStats.storesCount > 0 ? `${corruptedStats.storesCount} corrupted` : '0 (OK)'}
            </span>
            {#if corruptedStats.totalCount === 0}
              <span class="status-badge-healthy">✅ {$t('admin.store.auto_heal_healthy')}</span>
            {/if}
          </div>
        {/if}
      </div>

      <!-- Live Upload & Processing Status Card -->
      {#if isUploadingCSDI || activeJob}
        <div class="card progress-card {activeJob?.status === 'completed' ? 'completed' : ''} {activeJob?.status === 'failed' ? 'failed' : ''}">
          <div class="progress-header">
            <h4>
              {#if activeJob?.status === 'completed'}
                ✅ {$t('admin.csdi.import_complete')}
              {:else if activeJob?.status === 'failed'}
                ❌ {$t('admin.csdi.processing_error')}
              {:else if uploadPercent < 100}
                📤 {$t('admin.csdi.uploading_csv')}
              {:else}
                ⚙️ {$t('admin.csdi.processing_records')}
              {/if}
            </h4>
            <span class="progress-pct">
              {uploadPercent < 100 ? `${uploadPercent}% (Upload)` : `${activeJob?.percent || 10}%`}
            </span>
          </div>

          <div class="progress-bar-track">
            <div 
              class="progress-bar-fill {activeJob?.status === 'completed' ? 'fill-green' : activeJob?.status === 'failed' ? 'fill-red' : ''}" 
              style="width: {uploadPercent < 100 ? uploadPercent : (activeJob?.percent || 10)}%;">
            </div>
          </div>

          <div class="progress-detail-row">
            <span class="stage-text">
              {uploadPercent < 100 ? uploadStatusText : (activeJob?.stage || 'Processing records in memory...')}
            </span>
            {#if activeJob?.fileName}
              <small class="file-tag">📄 {activeJob.fileName}</small>
            {/if}
          </div>

          {#if activeJob && activeJob.totalRows > 0}
            <div class="live-stats-row">
              <span class="stat-pill">📄 {$t('admin.csdi.total_rows')}: {activeJob.totalRows.toLocaleString()}</span>
              <span class="stat-pill">⚡ {$t('admin.csdi.processed')}: {activeJob.processedRows.toLocaleString()}</span>
              <span class="stat-pill blue">🏪 {$t('admin.csdi.matched_stores')}: {activeJob.matchedRows.toLocaleString()}</span>
              <span class="stat-pill green">🟢 {$t('admin.csdi.high_confidence')}: {activeJob.highConfidence.toLocaleString()}</span>
              <span class="stat-pill gray">🔍 {$t('admin.csdi.unmatched_pois')}: {activeJob.unmatchedRows.toLocaleString()}</span>
            </div>
          {/if}
        </div>
      {/if}

      <!-- Discovered Missing Shopping Malls Card -->
      {#if isDiscoveringPlaces || discoveredPlaces.length > 0}
        <div class="card discovered-malls-card">
          <div class="discovered-header">
            <div class="discovered-title-area">
              <h4>
                🏬 {$t('admin.csdi.discovered_malls_title')} ({discoveredPlaces.length})
                {#if isDiscoveringPlaces}
                  <span class="scanning-tag">{$t('admin.csdi.scanning')}</span>
                {/if}
              </h4>
              <p class="hint">
                {$t('admin.csdi.discovered_malls_hint')}
              </p>
            </div>
            <div class="discovered-header-actions">
              <button class="secondary" on:click={fetchDiscoveredPlaces} disabled={isDiscoveringPlaces}>
                🔄 {$t('admin.csdi.rescan_malls')}
              </button>
              <button 
                class="primary add-malls-btn" 
                on:click={addSelectedDiscoveredPlaces} 
                disabled={selectedDiscoveredPlaces.size === 0 || isAddingPlaces}>
                {isAddingPlaces ? $t('admin.csdi.adding_malls') : `➕ ${$t('admin.csdi.add_selected_malls')} (${selectedDiscoveredPlaces.size})`}
              </button>
            </div>
          </div>

          {#if discoveredPlaces.length > 0}
            <div class="table-container discovered-table-wrap">
              <table class="data-table">
                <thead>
                  <tr>
                    <th style="width: 40px; text-align: center;">
                      <input 
                        type="checkbox" 
                        checked={selectedDiscoveredPlaces.size === discoveredPlaces.length && discoveredPlaces.length > 0}
                        on:change={toggleSelectAllDiscovered}
                      />
                    </th>
                    <th>{$t('admin.csdi.mall_name_en_zh')}</th>
                    <th>{$t('admin.csdi.short_tag')}</th>
                    <th>{$t('admin.csdi.district_address')}</th>
                    <th>{$t('admin.csdi.centroid_gps')}</th>
                    <th>{$t('admin.csdi.detected_stores')}</th>
                    <th style="width: 120px; text-align: center;">{$t('admin.csdi.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {#each discoveredPlaces as mall, idx}
                    <tr class="discovered-row {selectedDiscoveredPlaces.has(idx) ? 'row-selected' : ''}">
                      <td style="text-align: center;">
                        <input 
                          type="checkbox" 
                          checked={selectedDiscoveredPlaces.has(idx)} 
                          on:change={() => toggleSelectDiscovered(idx)}
                        />
                      </td>
                      <td>
                        <div class="input-stack">
                          <input 
                            type="text" 
                            bind:value={mall.nameEn} 
                            placeholder="English name (e.g. Telford Plaza)"
                            class="small-input"
                            on:input={() => {
                              if (mall.nameEn && mall.nameZh) mall.name = `${mall.nameEn} ${mall.nameZh}`;
                              else if (mall.nameEn) mall.name = mall.nameEn;
                            }}
                          />
                          <input 
                            type="text" 
                            bind:value={mall.nameZh} 
                            placeholder="Chinese name (e.g. 德福廣場)"
                            class="small-input"
                            on:input={() => {
                              if (mall.nameEn && mall.nameZh) mall.name = `${mall.nameEn} ${mall.nameZh}`;
                              else if (mall.nameZh) mall.name = mall.nameZh;
                            }}
                          />
                        </div>
                      </td>
                      <td>
                        <input type="text" bind:value={mall.shortTag} class="small-input shorttag-input" placeholder="Tag" />
                      </td>
                      <td>
                        <div class="addr-stack">
                          {#if mall.district}
                            <span class="district-badge">📍 {mall.district}</span>
                          {/if}
                          <input type="text" bind:value={mall.address} class="small-input" placeholder="Consensus address" />
                        </div>
                      </td>
                      <td>
                        {#if mall.lat && mall.lng}
                          <div class="gps-cell">
                            <code>{mall.lat.toFixed(4)}, {mall.lng.toFixed(4)}</code>
                            <a href="https://maps.google.com/?q={mall.lat},{mall.lng}" target="_blank" rel="noreferrer" class="map-link" title="Open Google Maps">🗺️</a>
                          </div>
                        {:else}
                          <span class="no-coords">{$t('admin.csdi.no_gps')}</span>
                        {/if}
                      </td>
                      <td>
                        <div class="store-badge-cell">
                          <span class="store-count-badge">🏬 {mall.storeCount}</span>
                          {#if mall.sampleStores && mall.sampleStores.length > 0}
                            <small class="sample-stores-hint" title={mall.sampleStores.join(', ')}>
                              {mall.sampleStores.slice(0, 2).join(', ')}{mall.sampleStores.length > 2 ? '...' : ''}
                            </small>
                          {/if}
                        </div>
                      </td>
                      <td style="text-align: center;">
                        <button 
                          class="secondary small-btn" 
                          on:click={() => addSingleDiscoveredPlace(mall)}
                          disabled={isAddingPlaces}>
                          ➕ {$t('admin.csdi.add_and_link')}
                        </button>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      {:else}
        <div class="card scan-malls-prompt">
          <div class="prompt-content">
            <span>💡 {$t('admin.csdi.scan_prompt')}</span>
            <button class="secondary small-btn" on:click={fetchDiscoveredPlaces} disabled={isDiscoveringPlaces}>
              {isDiscoveringPlaces ? $t('admin.csdi.scanning') : `🔍 ${$t('admin.csdi.scan_btn')}`}
            </button>
          </div>
        </div>
      {/if}

      <!-- Summary Metrics Badges -->
      <div class="metrics-grid">
        <div class="metric-card blue">
          <div class="metric-title">{$t('admin.csdi.pending_review')}</div>
          <div class="metric-value">{stagedSummary.pendingCount}</div>
        </div>
        <div class="metric-card green">
          <div class="metric-title">{$t('admin.csdi.high_confidence_safe')}</div>
          <div class="metric-value">{stagedSummary.highConfidence}</div>
        </div>
        <div class="metric-card yellow">
          <div class="metric-title">{$t('admin.csdi.needs_review')}</div>
          <div class="metric-value">{stagedSummary.needsReview}</div>
        </div>
        <div class="metric-card gray">
          <div class="metric-title">{$t('admin.csdi.already_applied')}</div>
          <div class="metric-value">{stagedSummary.appliedCount}</div>
        </div>
      </div>

      <!-- Action & Filter Bar -->
      <div class="card toolbar-card">
        <div class="toolbar-row">
          <div class="filters-group">
            <!-- Filter by Store -->
            <select bind:value={stagedFilterStore} on:change={() => fetchStagedRecords(1)}>
              <option value="">{$t('admin.csdi.all_stores')} ({stagedStores.length})</option>
              {#each stagedStores as sName}
                <option value={sName}>{sName}</option>
              {/each}
            </select>

            <!-- Filter by Status -->
            <select bind:value={stagedFilterStatus} on:change={() => fetchStagedRecords(1)}>
              <option value="pending">{$t('admin.csdi.status_pending')}</option>
              <option value="applied">{$t('admin.csdi.status_applied')}</option>
              <option value="rejected">{$t('admin.csdi.status_rejected')}</option>
              <option value="all">{$t('admin.csdi.all_statuses')}</option>
            </select>

            <!-- Filter by Confidence -->
            <select bind:value={stagedFilterConfidence} on:change={() => fetchStagedRecords(1)}>
              <option value="all">{$t('admin.csdi.all_confidences')}</option>
              <option value="HIGH">{$t('admin.csdi.high_confidence_only')}</option>
              <option value="MEDIUM">{$t('admin.csdi.medium_confidence')}</option>
              <option value="LOW">{$t('admin.csdi.low_confidence')}</option>
            </select>

            <!-- Search -->
            <input 
              type="text" 
              placeholder={$t('admin.csdi.search_address_store')} 
              bind:value={stagedSearch} 
              on:keydown={(e) => e.key === 'Enter' && fetchStagedRecords(1)}
              style="width: 200px;" 
            />
            <button class="secondary" on:click={() => fetchStagedRecords(1)} style="padding: 0.5rem 0.8rem;">🔍</button>
          </div>

          <div class="actions-group">
            <button 
              class="quick-btn" 
              disabled={stagedSummary.highConfidence === 0 || isStagedLoading} 
              on:click={quickApplyHighConfidence}>
              ⚡ {$t('admin.csdi.quick_apply')} ({stagedSummary.highConfidence})
            </button>
            <button 
              class="primary" 
              disabled={selectedStagedIds.size === 0 || isStagedLoading} 
              on:click={applySelectedStaged}>
              {$t('admin.csdi.apply_selected')} ({selectedStagedIds.size})
            </button>
            <button 
              class="danger-btn" 
              disabled={selectedStagedIds.size === 0 || isStagedLoading} 
              on:click={rejectSelectedStaged}>
              {$t('admin.csdi.reject_selected')}
            </button>
          </div>
        </div>
      </div>

      <!-- Staged Records Table -->
      <div class="card table-card">
        {#if isStagedLoading}
          <p class="loading-hint">{$t('common.loading')}</p>
        {:else if stagedRecords.length === 0}
          <div class="empty-state">
            <p>{$t('admin.csdi.no_staged_records')}</p>
          </div>
        {:else}
          <div class="table-responsive">
            <table class="staged-table">
              <thead>
                <tr>
                  <th style="width: 40px; text-align: center;">
                    <input 
                      type="checkbox" 
                      checked={stagedRecords.length > 0 && selectedStagedIds.size === stagedRecords.length}
                      on:change={toggleSelectAllStaged}
                    />
                  </th>
                  <th>{$t('admin.csdi.th_store')}</th>
                  <th>{$t('admin.csdi.th_address')}</th>
                  <th>{$t('admin.csdi.th_action_override')}</th>
                  <th>{$t('admin.csdi.associated_mall')}</th>
                  <th>{$t('admin.csdi.th_confidence_reason')}</th>
                  <th>{$t('admin.csdi.high_confidence')}</th>
                  <th>Status</th>
                  <th>{$t('admin.csdi.th_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {#each stagedRecords as rec (rec._id)}
                  <tr class="row-{rec.confidence.toLowerCase()} {rec.status}">
                    <td style="text-align: center;">
                      <input 
                        type="checkbox" 
                        checked={selectedStagedIds.has(rec._id)} 
                        on:change={() => toggleSelectStaged(rec._id)}
                      />
                    </td>
                    <td>
                      <div class="store-badge-cell">
                        <strong>{rec.storeName}</strong>
                        {#if rec.matchedAlias && rec.matchedAlias !== rec.storeName}
                          <small class="alias-hint">matched: {rec.matchedAlias}</small>
                        {/if}
                      </div>
                    </td>
                    <td>
                      <div class="addr-cell">
                        <span>{rec.addressEn || rec.addressZh}</span>
                        {#if rec.coordinates && rec.coordinates.coordinates}
                          <a 
                            href={`https://maps.google.com/?q=${rec.coordinates.coordinates[1]},${rec.coordinates.coordinates[0]}`} 
                            target="_blank" 
                            class="gps-link">
                            📍 {rec.coordinates.coordinates[1].toFixed(4)}, {rec.coordinates.coordinates[0].toFixed(4)}
                          </a>
                        {/if}
                      </div>
                    </td>
                    <td>
                      <span class="action-tag {stagedOverrides[rec._id]?.selectedAction || rec.suggestedAction}">
                        {stagedOverrides[rec._id]?.selectedAction || rec.suggestedAction}
                      </span>
                    </td>
                    <td>
                      <!-- Editable Mall Selection Dropdown -->
                      <select 
                        class="mall-select"
                        value={stagedOverrides[rec._id]?.selectedPlace || rec.proposedPlaceName || ''}
                        on:change={(e) => handleMallOverride(rec._id, e.target.value)}>
                        <option value="">({$t('admin.csdi.street_shop_none')})</option>
                        {#each allPlaces as pl}
                          <option value={pl.name}>{pl.nameEn || pl.name}{pl.nameZh && pl.nameZh !== (pl.nameEn || pl.name) ? ` (${pl.nameZh})` : ''}</option>
                        {/each}
                      </select>
                    </td>
                    <td>
                      <div class="reason-cell">
                        <small>{rec.matchReason}</small>
                      </div>
                    </td>
                    <td>
                      <span class="confidence-badge {rec.confidence}">
                        {rec.confidence}
                      </span>
                    </td>
                    <td>
                      <span class="status-badge {rec.status}">{rec.status}</span>
                    </td>
                    <td>
                      <div class="row-actions">
                        {#if rec.status === 'pending'}
                          <button class="mini-btn apply" title={$t('admin.csdi.btn_apply')} on:click={() => applySingleStaged(rec)}>✓</button>
                          <button class="mini-btn reject" title={$t('admin.csdi.btn_reject')} on:click={() => rejectSingleStaged(rec)}>✕</button>
                        {:else}
                          <small style="color: #888;">{rec.status}</small>
                        {/if}
                      </div>
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="pagination-row">
            <button class="secondary" disabled={stagedPage === 1} on:click={() => fetchStagedRecords(stagedPage - 1)}>
              &larr; {$t('common.prev')}
            </button>
            <span>{$t('common.page_of', { page: stagedPage, total: Math.ceil(stagedTotal / stagedLimit) || 1 })} ({stagedTotal} total)</span>
            <button class="secondary" disabled={stagedPage >= Math.ceil(stagedTotal / stagedLimit)} on:click={() => fetchStagedRecords(stagedPage + 1)}>
              {$t('common.next')} &rarr;
            </button>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  <!-- ============================================================== -->
  <!-- TAB 3: UNMATCHED CSDI POIS ("CREATE NEW STORE")                -->
  <!-- ============================================================== -->
  {#if activeTab === 'csdi_unmatched'}
    <div class="csdi-container">
      <div class="card intro-card">
        <h3>🔍 {$t('admin.csdi.unmatched_title')}</h3>
        <p class="hint">
          {$t('admin.csdi.unmatched_hint')}
        </p>

        <div class="search-bar-row">
          <input 
            type="text" 
            placeholder={$t('admin.csdi.unmatched_search_placeholder')} 
            bind:value={unmatchedSearch} 
            on:keydown={(e) => e.key === 'Enter' && fetchUnmatchedRecords(1)}
            style="flex: 1; padding: 0.6rem;"
          />
          <button class="primary" on:click={() => fetchUnmatchedRecords(1)}>🔍</button>
        </div>
      </div>

      <div class="card table-card">
        {#if isUnmatchedLoading}
          <p class="loading-hint">{$t('common.loading')}</p>
        {:else if unmatchedRecords.length === 0}
          <div class="empty-state">
            <p>{$t('admin.csdi.no_staged_records')}</p>
          </div>
        {:else}
          <div class="table-responsive">
            <table class="staged-table">
              <thead>
                <tr>
                  <th>{$t('admin.csdi.th_poi_name')}</th>
                  <th>{$t('admin.csdi.th_poi_address')}</th>
                  <th>{$t('admin.csdi.th_poi_gps')}</th>
                  <th>{$t('admin.csdi.th_nearest_mall')}</th>
                  <th>{$t('admin.csdi.action')}</th>
                </tr>
              </thead>
              <tbody>
                {#each unmatchedRecords as rec (rec._id)}
                  <tr>
                    <td>
                      <strong>{rec.csdiNameEn || rec.csdiNameZh}</strong>
                      {#if rec.csdiNameZh && rec.csdiNameEn && rec.csdiNameZh !== rec.csdiNameEn}
                        <br><small style="color: #666;">{rec.csdiNameZh}</small>
                      {/if}
                    </td>
                    <td>
                      <div>{rec.addressEn || rec.addressZh}</div>
                      {#if rec.district}<small class="district-badge">{rec.district}</small>{/if}
                    </td>
                    <td>
                      {#if rec.coordinates && rec.coordinates.coordinates}
                        <a 
                          href={`https://maps.google.com/?q=${rec.coordinates.coordinates[1]},${rec.coordinates.coordinates[0]}`} 
                          target="_blank" 
                          class="gps-link">
                          📍 {rec.coordinates.coordinates[1].toFixed(4)}, {rec.coordinates.coordinates[0].toFixed(4)}
                        </a>
                      {:else}
                        <span style="color: #888;">N/A</span>
                      {/if}
                    </td>
                    <td>
                      {#if rec.proposedPlaceName}
                        <span class="mall-proximity-tag">
                          🏬 {rec.proposedPlaceName} ({rec.distanceMeters.toFixed(0)}m)
                        </span>
                      {:else}
                        <span style="color: #888;">({$t('admin.csdi.street_shop_none')})</span>
                      {/if}
                    </td>
                    <td>
                      <button class="primary mini-btn-action" on:click={() => openCreateStoreModal(rec)}>
                        ➕ {$t('admin.csdi.btn_create_store')}
                      </button>
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="pagination-row">
            <button class="secondary" disabled={unmatchedPage === 1} on:click={() => fetchUnmatchedRecords(unmatchedPage - 1)}>
              &larr; {$t('common.prev')}
            </button>
            <span>{$t('common.page_of', { page: unmatchedPage, total: Math.ceil(unmatchedTotal / unmatchedLimit) || 1 })} ({unmatchedTotal} total)</span>
            <button class="secondary" disabled={unmatchedPage >= Math.ceil(unmatchedTotal / unmatchedLimit)} on:click={() => fetchUnmatchedRecords(unmatchedPage + 1)}>
              {$t('common.next')} &rarr;
            </button>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  <!-- ============================================================== -->
  <!-- TAB 4: TRACK RECORDS                                           -->
  <!-- ============================================================== -->
  {#if activeTab === 'track_records'}
    <div class="card track-records-section">
      <h3>{$t('admin.store.track_title')} ({trTotal})</h3>
      {#if isTrLoading}
        <p>{$t('common.loading')}</p>
      {:else if trackRecords.length === 0}
        <p>{$t('admin.store.no_track_records')}</p>
      {:else}
        <ul class="track-record-list">
          {#each trackRecords as record (record._id)}
            <li class="track-record-item" on:click={() => populateFromTrackRecord(record)} style="cursor: pointer; display: flex; flex-direction: column; gap: 0.3rem; padding: 0.8rem 0; border-bottom: 1px solid #eee;">
              <div class="info" style="display: flex; flex-direction: row; justify-content: space-between; align-items: center; width: 100%;">
                <div style="display: flex; align-items: center; gap: 0.5rem;">
                  <span class="store-name" style="font-size: 1rem; font-weight: 600;">{record.storeName}</span>
                  <span class="action-badge {record.action}">{record.action}</span>
                </div>
                <span class="timestamp" style="font-size: 0.8rem; color: #888;">{new Date(record.createdAt).toLocaleString()}</span>
              </div>
            </li>
          {/each}
        </ul>
        <div class="pagination-row" style="margin-top: 1rem; display: flex; justify-content: space-between; align-items: center;">
          <button class="secondary" disabled={trPage === 1} on:click={() => fetchTrackRecords(trPage - 1)} style="padding: 4px 10px; font-size: 0.8rem;">&larr; {$t('common.prev')}</button>
          <span style="font-size: 0.9rem; color: #555;">{$t('common.page_of', { page: trPage, total: Math.ceil(trTotal / trLimit) || 1 })}</span>
          <button class="secondary" disabled={trPage >= Math.ceil(trTotal / trLimit)} on:click={() => fetchTrackRecords(trPage + 1)} style="padding: 4px 10px; font-size: 0.8rem;">{$t('common.next')} &rarr;</button>
        </div>
      {/if}
    </div>
  {/if}

  <!-- ============================================================== -->
  <!-- MODAL: CREATE STORE FROM UNMATCHED CSDI POI                   -->
  <!-- ============================================================== -->
  {#if showCreateStoreModal}
    <div class="modal-backdrop">
      <div class="modal-box">
        <h3>➕ {$t('admin.csdi.modal_create_store_title')}</h3>
        <p class="hint" style="margin-bottom: 1rem;">
          {$t('admin.csdi.modal_create_store_hint')}
        </p>

        <form on:submit|preventDefault={submitCreateStoreFromUnmatched}>
          <div class="form-group">
            <label for="new-store-name">{$t('admin.store.name')}</label>
            <input type="text" id="new-store-name" bind:value={newStoreName} required />
          </div>

          <div class="form-group">
            <label for="new-store-aliases">{$t('admin.store.aliases')}</label>
            <input type="text" id="new-store-aliases" bind:value={newStoreAliases} placeholder={$t('admin.store.aliases_placeholder')} />
          </div>

          <div class="form-group">
            <label for="new-store-country">{$t('admin.store.country')}</label>
            <input type="text" id="new-store-country" bind:value={newStoreCountry} required />
          </div>

          <div class="form-group">
            <label>{$t('admin.categories')}</label>
            <div class="category-scroll">
              {#each allCategories as cat}
                <label class="checkbox-label">
                  <input type="checkbox" value={cat.name} bind:group={newStoreCategories} />
                  <span>{cat.name}</span>
                </label>
              {/each}
            </div>
          </div>

          <div class="form-group">
            <label for="new-store-place">{$t('admin.csdi.associated_mall')}</label>
            <select id="new-store-place" bind:value={newStorePlace} style="width: 100%; padding: 0.6rem; border: 1px solid #ccc; border-radius: 4px;">
              <option value="">({$t('admin.csdi.street_shop_none')})</option>
              {#each allPlaces as pl}
                <option value={pl.name}>{pl.nameEn || pl.name}{pl.nameZh && pl.nameZh !== (pl.nameEn || pl.name) ? ` (${pl.nameZh})` : ''}</option>
              {/each}
            </select>
          </div>

          <div class="form-group">
            <label for="new-store-address">{$t('admin.csdi.initial_address')}</label>
            <input type="text" id="new-store-address" bind:value={newStoreAddress} required />
          </div>

          <div class="coord-row" style="margin-bottom: 1.5rem;">
            <div style="flex: 1;">
              <label>{$t('admin.store.lat')}</label>
              <input type="number" step="any" bind:value={newStoreLat} />
            </div>
            <div style="flex: 1;">
              <label>{$t('admin.store.lng')}</label>
              <input type="number" step="any" bind:value={newStoreLng} />
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="secondary" on:click={closeCreateStoreModal}>{$t('common.cancel')}</button>
            <button type="submit" class="primary" disabled={isCreatingStore}>
              {isCreatingStore ? $t('admin.csdi.creating') : $t('admin.csdi.btn_create_link')}
            </button>
          </div>
        </form>
      </div>
    </div>
  {/if}
</div>

<style>
  .maintenance-container { max-width: 1280px; margin: 2rem auto; padding: 0 1rem; }
  
  /* Navigation Tab Bar */
  .tab-bar {
    display: flex;
    gap: 0.5rem;
    border-bottom: 2px solid #dee2e6;
    margin-bottom: 1.5rem;
    overflow-x: auto;
  }
  .tab-btn {
    background: none;
    border: none;
    padding: 0.8rem 1.2rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: #495057;
    cursor: pointer;
    border-bottom: 3px solid transparent;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
    transition: all 0.2s;
  }
  .tab-btn:hover {
    color: #007bff;
    background-color: #f8f9fa;
  }
  .tab-btn.active {
    color: #007bff;
    border-bottom-color: #007bff;
    background-color: #f1f7ff;
    border-radius: 4px 4px 0 0;
  }
  .tab-badge {
    background: #007bff;
    color: white;
    font-size: 0.75rem;
    padding: 2px 7px;
    border-radius: 9999px;
    font-weight: bold;
  }
  .tab-badge.gray {
    background: #6c757d;
  }
  .tab-badge.purple {
    background: #7c3aed;
  }

  .grid-layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
  @media(min-width: 900px) { .grid-layout { grid-template-columns: 450px 1fr; } }
  
  .card { background: #fff; padding: 1.5rem; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); margin-bottom: 1.5rem; }
  
  h3 { margin-top: 0; color: #0056b3; border-bottom: 1px solid #eee; padding-bottom: 0.5rem; margin-bottom: 1rem; }
  .hint { color: #6c757d; font-size: 0.88rem; margin-top: -0.5rem; margin-bottom: 1rem; }
  
  .form-group { margin-bottom: 1rem; }
  label { display: block; margin-bottom: 0.4rem; font-weight: 600; color: #444; font-size: 0.9rem; }
  input[type="text"], input[type="number"], select { width: 100%; padding: 0.6rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
  
  .category-scroll { max-height: 120px; overflow-y: auto; border: 1px solid #eee; padding: 0.5rem; border-radius: 4px; background: #fafafa; }
  .checkbox-label { display: flex; align-items: center; gap: 0.5rem; font-weight: normal; margin-bottom: 0.2rem; cursor: pointer; }

  .locations-section { border: 1px solid #e0e0e0; padding: 1rem; border-radius: 6px; margin-bottom: 1.5rem; background: #fdfdfd; }
  .location-list { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
  .location-pill { display: flex; align-items: center; background: #eef2f7; padding: 0.5rem; border-radius: 4px; font-size: 0.85rem; }
  .remove-loc { background: none; border: none; color: #dc3545; cursor: pointer; font-size: 1.2rem; font-weight: bold; margin-left: auto; }

  .location-add-form { display: flex; flex-direction: column; gap: 0.5rem; margin-top: 0.5rem; }
  .address-lookup-row { display: flex; gap: 0.5rem; }
  .lookup-btn { background: #6c757d; color: white; border: none; padding: 0 0.8rem; border-radius: 4px; cursor: pointer; }
  .lookup-btn:disabled { opacity: 0.5; }

  .coord-row { display: flex; gap: 0.5rem; }
  .add-loc-btn { background: #28a745; color: white; border: none; padding: 0 1rem; border-radius: 4px; cursor: pointer; font-weight: bold; }

  .form-actions { display: flex; justify-content: flex-end; gap: 0.5rem; border-top: 1px solid #eee; padding-top: 1rem; }
  button { padding: 0.6rem 1.2rem; border: none; border-radius: 4px; cursor: pointer; font-weight: 600; }
  .primary { background-color: #007bff; color: white; }
  .secondary { background-color: #6c757d; color: white; }
  
  .list-wrapper { max-height: 800px; overflow-y: auto; }
  ul { list-style: none; padding: 0; margin: 0; }
  li { display: flex; justify-content: space-between; align-items: flex-start; padding: 1rem 0; border-bottom: 1px solid #eee; }
  .info { display: flex; flex-direction: column; gap: 0.3rem; }
  .store-name { font-weight: bold; font-size: 1.1rem; }
  .country-badge { font-size: 0.75rem; background: #333; color: white; padding: 2px 6px; border-radius: 4px; margin-left: 5px; }
  .loc-count { font-size: 0.8rem; color: #666; }

  .item-actions { display: flex; gap: 0.4rem; }
  .edit-btn { background-color: #ffc107; color: #212529; padding: 4px 10px; font-size: 0.8rem; }
  .delete-btn { background-color: #dc3545; color: white; padding: 4px 10px; font-size: 0.8rem; }

  .alert-msg { padding: 0.8rem; border-radius: 4px; margin-bottom: 1rem; }
  .success { background-color: #def7ec; color: #03543f; font-weight: 600; }
  .error { background-color: #fde8e8; color: #9b1c1c; font-weight: 600; }
  .aliases-badge { font-size: 0.8rem; background: #e2e8f0; color: #4a5568; padding: 2px 6px; border-radius: 4px; margin-left: 10px; display: inline-block; }

  .list-column { display: flex; flex-direction: column; gap: 1.5rem; }

  .action-badge {
    font-size: 0.75rem;
    font-weight: bold;
    padding: 2px 8px;
    border-radius: 9999px;
    text-transform: capitalize;
    display: inline-block;
  }
  .action-badge.added { background-color: #def7ec; color: #03543f; }
  .action-badge.updated { background-color: #e1effe; color: #1e429f; }
  .track-record-item:hover { background-color: #f7fafc; }

  /* ============================================================== */
  /* CSDI IMPORTER STYLES                                           */
  /* ============================================================== */
  .upload-box { border-left: 4px solid #007bff; }
  .upload-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem; }
  .upload-btn {
    background: #007bff;
    color: white;
    padding: 0.7rem 1.4rem;
    border-radius: 4px;
    font-weight: 600;
    cursor: pointer;
    display: inline-block;
    transition: background 0.2s;
  }
  .upload-btn:hover { background: #0056b3; }
  .upload-btn.disabled { opacity: 0.6; pointer-events: none; }

  /* Summary Metrics Cards */
  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  .metric-card {
    background: white;
    padding: 1.2rem;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  .metric-card.blue { border-top: 4px solid #007bff; }
  .metric-card.green { border-top: 4px solid #28a745; }
  .metric-card.yellow { border-top: 4px solid #ffc107; }
  .metric-card.gray { border-top: 4px solid #6c757d; }
  .metric-title { font-size: 0.85rem; color: #666; font-weight: 600; }
  .metric-value { font-size: 1.8rem; font-weight: bold; color: #212529; }

  /* Toolbar */
  .toolbar-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }
  .filters-group { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
  .actions-group { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
  .quick-btn { background: #28a745; color: white; font-weight: bold; }
  .quick-btn:hover { background: #218838; }
  .quick-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .danger-btn { background: #dc3545; color: white; }
  .danger-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Staged Table */
  .table-responsive { overflow-x: auto; }
  .staged-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
    text-align: left;
  }
  .staged-table th {
    background: #f8f9fa;
    padding: 0.75rem 0.5rem;
    border-bottom: 2px solid #dee2e6;
    color: #495057;
    font-weight: 600;
  }
  .staged-table td {
    padding: 0.75rem 0.5rem;
    border-bottom: 1px solid #eee;
    vertical-align: middle;
  }
  .staged-table tbody tr:hover { background-color: #f8fbff; }

  .store-badge-cell strong { font-size: 0.95rem; color: #1e3a8a; }
  .alias-hint { display: block; font-size: 0.75rem; color: #6b7280; font-style: italic; }
  .addr-cell span { display: block; max-width: 280px; }
  .gps-link { font-size: 0.75rem; color: #0284c7; text-decoration: none; display: inline-block; margin-top: 2px; }
  .gps-link:hover { text-decoration: underline; }

  .action-tag {
    font-size: 0.75rem;
    font-weight: bold;
    padding: 2px 6px;
    border-radius: 4px;
    text-transform: uppercase;
  }
  .action-tag.MALL { background: #e0f2fe; color: #0369a1; }
  .action-tag.STREET { background: #f3f4f6; color: #374151; }

  .mall-select {
    padding: 0.4rem;
    font-size: 0.85rem;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    max-width: 200px;
    background: white;
  }

  .confidence-badge {
    font-size: 0.75rem;
    font-weight: bold;
    padding: 3px 8px;
    border-radius: 9999px;
  }
  .confidence-badge.HIGH { background: #dcfce7; color: #15803d; }
  .confidence-badge.MEDIUM { background: #fef9c3; color: #854d0e; }
  .confidence-badge.LOW { background: #fee2e2; color: #b91c1c; }

  .status-badge {
    font-size: 0.75rem;
    padding: 2px 6px;
    border-radius: 4px;
    text-transform: capitalize;
  }
  .status-badge.pending { background: #eff6ff; color: #1d4ed8; }
  .status-badge.applied { background: #f0fdf4; color: #166534; font-weight: bold; }
  .status-badge.rejected { background: #fef2f2; color: #991b1b; }

  .row-actions { display: flex; gap: 0.3rem; }
  .mini-btn {
    border: none;
    border-radius: 4px;
    padding: 4px 8px;
    cursor: pointer;
    font-weight: bold;
    font-size: 0.8rem;
  }
  .mini-btn.apply { background: #22c55e; color: white; }
  .mini-btn.reject { background: #ef4444; color: white; }
  .mini-btn-action { padding: 4px 8px; font-size: 0.8rem; }

  .pagination-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
    font-size: 0.85rem;
    color: #64748b;
  }

  .district-badge {
    background: #f1f5f9;
    color: #475569;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75rem;
    display: inline-block;
    margin-top: 2px;
  }
  .mall-proximity-tag {
    background: #fdf4ff;
    color: #86198f;
    font-size: 0.8rem;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    display: inline-block;
  }

  /* Modal */
  .modal-backdrop {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 1rem;
  }
  .modal-box {
    background: white;
    border-radius: 8px;
    max-width: 600px;
    width: 100%;
    padding: 1.5rem;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  }

  .search-bar-row { display: flex; gap: 0.5rem; margin-top: 1rem; }
  .empty-state { text-align: center; padding: 2rem; color: #64748b; }
  .loading-hint { text-align: center; padding: 2rem; color: #0284c7; }

  /* Progress Card Styles */
  .progress-card {
    border-left: 4px solid #007bff;
    background: #f8fafc;
    animation: fadeIn 0.3s ease-in;
  }
  .progress-card.completed { border-left-color: #22c55e; background: #f0fdf4; }
  .progress-card.failed { border-left-color: #ef4444; background: #fef2f2; }
  .progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }
  .progress-header h4 { margin: 0; color: #1e293b; font-size: 1.05rem; }
  .progress-pct { font-weight: bold; color: #0284c7; font-size: 1.1rem; }
  .progress-bar-track {
    width: 100%;
    height: 10px;
    background: #e2e8f0;
    border-radius: 9999px;
    overflow: hidden;
    margin: 0.5rem 0;
  }
  .progress-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #0284c7, #38bdf8);
    transition: width 0.3s ease;
  }
  .progress-bar-fill.fill-green { background: #22c55e; }
  .progress-bar-fill.fill-red { background: #ef4444; }
  .progress-detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.85rem;
    color: #475569;
    margin-top: 0.4rem;
  }
  .stage-text { font-weight: 500; }
  .file-tag { background: #e2e8f0; padding: 2px 6px; border-radius: 4px; font-size: 0.75rem; }
  .live-stats-row {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-top: 0.8rem;
    padding-top: 0.8rem;
    border-top: 1px dashed #cbd5e1;
  }
  .stat-pill {
    background: #ffffff;
    border: 1px solid #cbd5e1;
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #334155;
  }
  .stat-pill.blue { border-color: #93c5fd; color: #1d4ed8; background: #eff6ff; }
  .stat-pill.green { border-color: #86efac; color: #15803d; background: #f0fdf4; }
  .stat-pill.gray { border-color: #cbd5e1; color: #64748b; background: #f8fafc; }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* Discovered Malls Card */
  .discovered-malls-card {
    border: 1px solid #c084fc;
    background: #faf5ff;
    animation: fadeIn 0.3s ease-in;
    margin-bottom: 1.5rem;
  }
  .discovered-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
  }
  .discovered-title-area h4 {
    margin: 0 0 0.25rem 0;
    color: #581c87;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .scanning-tag {
    font-size: 0.75rem;
    background: #e9d5ff;
    color: #6b21a8;
    padding: 2px 8px;
    border-radius: 9999px;
  }
  .discovered-header-actions {
    display: flex;
    gap: 0.5rem;
  }
  .add-malls-btn {
    background: #7c3aed !important;
    border-color: #6d28d9 !important;
    color: white !important;
    font-weight: 600;
  }
  .add-malls-btn:hover:not(:disabled) {
    background: #6d28d9 !important;
  }
  .discovered-table-wrap {
    background: white;
    border-radius: 6px;
    border: 1px solid #e9d5ff;
  }
  .discovered-row:hover {
    background: #fbf8ff;
  }
  .row-selected {
    background: #f5f3ff !important;
  }
  .input-stack {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .addr-stack {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .district-badge {
    background: #f1f5f9;
    color: #475569;
    font-size: 0.75rem;
    padding: 1px 6px;
    border-radius: 4px;
    display: inline-block;
    width: fit-content;
  }
  .shorttag-input {
    width: 110px;
    font-family: monospace;
    font-size: 0.8rem;
  }
  .store-badge-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .store-count-badge {
    background: #ede9fe;
    color: #6d28d9;
    font-size: 0.8rem;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    display: inline-block;
    width: fit-content;
  }
  .sample-stores-hint {
    color: #64748b;
    font-size: 0.75rem;
  }
  .gps-cell {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .map-link {
    text-decoration: none;
    font-size: 0.9rem;
  }
  .no-coords {
    color: #94a3b8;
    font-size: 0.8rem;
    font-style: italic;
  }
  .scan-malls-prompt {
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    margin-bottom: 1.5rem;
    padding: 0.8rem 1.2rem;
  }
  .prompt-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    font-size: 0.9rem;
    color: #334155;
  }

  /* Auto-Heal Banner and Card Styles */
  .auto-heal-banner {
    margin-bottom: 1.25rem;
    padding: 1rem 1.25rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  .auto-heal-banner.warning {
    background: #fffbeb;
    border: 1px solid #fde68a;
  }
  .auto-heal-banner.success {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
  }
  .auto-heal-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .auto-heal-info {
    display: flex;
    align-items: center;
    gap: 0.85rem;
  }
  .auto-heal-icon {
    font-size: 1.6rem;
    flex-shrink: 0;
  }
  .auto-heal-info h4 {
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
    color: #1e293b;
  }
  .auto-heal-desc {
    margin: 0;
    font-size: 0.88rem;
    color: #475569;
  }
  .auto-heal-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .auto-heal-btn {
    padding: 0.55rem 1.1rem;
    font-size: 0.88rem;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    border: none;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    white-space: nowrap;
  }
  .auto-heal-btn.btn-warning {
    background: #d97706;
    color: white;
  }
  .auto-heal-btn.btn-warning:hover:not(:disabled) {
    background: #b45309;
  }
  .auto-heal-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .auto-heal-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1.2rem;
    margin-bottom: 1.5rem;
  }
  .auto-heal-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 0.8rem;
  }
  .auto-heal-card-header h4 {
    margin: 0 0 0.25rem 0;
    font-size: 1.05rem;
  }
  .auto-heal-card-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .health-stats-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid #f1f5f9;
  }
  .stat-pill.pill-warning {
    background: #fef3c7;
    color: #92400e;
    font-weight: 600;
  }
  .stat-pill.pill-ok {
    background: #ecfdf5;
    color: #065f46;
  }
  .status-badge-healthy {
    font-size: 0.85rem;
    color: #15803d;
    font-weight: 500;
    margin-left: 0.5rem;
  }
  .spinner-small {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    display: inline-block;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .pulse-btn {
    animation: pulseGlow 2s infinite;
  }
  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.4); }
    50% { box-shadow: 0 0 0 6px rgba(217, 119, 6, 0); }
  }
  .mode-tab-btn {
    padding: 0.4rem 0.85rem;
    font-size: 0.85rem;
    font-weight: 500;
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    color: #475569;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
  }
  .mode-tab-btn:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
  }
  .mode-tab-btn.active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.25);
  }
</style>